import { useTranslation } from "react-i18next";
import { sectionHeadingStyle, labelStyle } from "./eventSectionStyles.js";
import { EVENT_CHARACTERISTICS, CHARACTERISTIC_MAX, readCharacteristic } from "./characteristics.js";

/**
 * "How much combat, how much magic, how much puzzle" — the thing people
 * actually want to know before booking, and the part of an event
 * description that is hardest to write in prose.
 *
 * The five axes are fixed (see characteristics.js) so two events can be
 * compared; an axis the organisers didn't rate is left out rather than
 * shown as zero, which would claim something they never said.
 *
 * The bars are decoration: `aria-hidden`, with the same information as
 * text in the row above, so a screen reader hears "Ambiente 5 von 5"
 * instead of five unlabelled boxes.
 */
export default function EventCharacteristics({ characteristics }) {
  const { t } = useTranslation();

  const rows = EVENT_CHARACTERISTICS.map((key) => ({ key, ...readCharacteristic(characteristics, key) })).filter(
    (row) => row.value != null
  );
  if (rows.length === 0) return null;

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "22px" }}>
      <h2 style={sectionHeadingStyle}>{t("events.characteristicsTitle")}</h2>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(200px, 100%), 1fr))",
          gap: "28px 40px",
          background: "var(--color-bg-raised)",
          borderRadius: "var(--radius-card)",
          padding: "32px",
        }}
      >
        {rows.map((row) => {
          const value = Math.max(0, Math.min(row.value, CHARACTERISTIC_MAX));
          return (
            <div key={row.key} style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "12px" }}>
                <span style={{ fontSize: "14px", color: "var(--color-cream)" }}>{t(`events.characteristic.${row.key}`)}</span>
                <span
                  style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "rgba(254,250,239,0.55)" }}
                  aria-label={t("events.characteristicScaleA11y", { value, max: CHARACTERISTIC_MAX })}
                >
                  {t("events.characteristicScale", { value, max: CHARACTERISTIC_MAX })}
                </span>
              </div>
              <div aria-hidden="true" style={{ display: "flex", gap: "4px" }}>
                {Array.from({ length: CHARACTERISTIC_MAX }, (_, i) => (
                  <span
                    key={i}
                    style={{
                      width: "26px",
                      height: "6px",
                      borderRadius: "2px",
                      background: i < value ? "var(--color-gold)" : "rgba(254,250,239,0.14)",
                    }}
                  />
                ))}
              </div>
              {row.note && (
                <span style={{ ...labelStyle, fontSize: "12px", letterSpacing: "normal", textTransform: "none", lineHeight: 1.5 }}>
                  {row.note}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
